import axios from 'axios';
import { 
  ApiResponse, 
  Movie, 
  TVSeries, 
  DetailedMovie, 
  DetailedTVSeries, 
  Credits, 
  Genre,
  SearchFilters 
} from '../types';

// TMDb API Configuration from environment variables
const API_BASE_URL = process.env.REACT_APP_TMDB_BASE_URL || 'https://api.themoviedb.org/3';
const API_KEY = process.env.REACT_APP_TMDB_API_KEY || 'YOUR_TMDB_API_KEY';
const IMAGE_BASE_URL = process.env.REACT_APP_TMDB_IMAGE_BASE_URL || 'https://image.tmdb.org/t/p';

// Create axios instance with default config
const tmdbApi = axios.create({
  baseURL: API_BASE_URL,
  params: {
    api_key: API_KEY,
    language: 'en-US',
  },
});

// Helper function to get image URL
export const getImageUrl = (path: string | null, size: string = 'w500'): string => {
  if (!path) return '/placeholder-poster.jpg';
  return `${IMAGE_BASE_URL}/${size}${path}`;
};

// Helper function to get backdrop URL
export const getBackdropUrl = (path: string | null, size: string = 'w1280'): string => {
  if (!path) return '/placeholder-backdrop.jpg';
  return `${IMAGE_BASE_URL}/${size}${path}`;
};

// API Functions

/**
 * Get trending movies and TV shows
 */
export const getTrending = async (timeWindow: 'day' | 'week' = 'week'): Promise<ApiResponse<Movie | TVSeries>> => {
  try {
    const response = await tmdbApi.get(`/trending/all/${timeWindow}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching trending:', error);
    throw error;
  }
};

/**
 * Get popular movies
 */
export const getPopularMovies = async (page: number = 1): Promise<ApiResponse<Movie>> => {
  try {
    const response = await tmdbApi.get('/movie/popular', {
      params: { page }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching popular movies:', error);
    throw error;
  }
};

/**
 * Get popular TV series
 */
export const getPopularTVSeries = async (page: number = 1): Promise<ApiResponse<TVSeries>> => {
  try {
    const response = await tmdbApi.get('/tv/popular', {
      params: { page }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching popular TV series:', error);
    throw error;
  }
};

/**
 * Search for movies and TV shows
 */
export const searchMulti = async (
  query: string, 
  page: number = 1,
  filters?: Partial<SearchFilters>
): Promise<ApiResponse<Movie | TVSeries>> => {
  try {
    const params: any = { 
      query, 
      page,
      include_adult: false 
    };

    // Add filters if provided
    if (filters?.year) {
      params.year = filters.year;
    }
    if (filters?.rating) {
      params['vote_average.gte'] = filters.rating;
    }

    const response = await tmdbApi.get('/search/multi', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching:', error);
    throw error;
  }
};

/**
 * Search for movies only
 */
export const searchMovies = async (
  query: string, 
  page: number = 1,
  filters?: Partial<SearchFilters>
): Promise<ApiResponse<Movie>> => {
  try {
    const params: any = { 
      query, 
      page,
      include_adult: false 
    };

    if (filters?.year) {
      params.year = filters.year;
    }
    if (filters?.rating) {
      params['vote_average.gte'] = filters.rating;
    }

    const response = await tmdbApi.get('/search/movie', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching movies:', error);
    throw error;
  }
};

/**
 * Search for TV series only
 */
export const searchTVSeries = async (
  query: string, 
  page: number = 1,
  filters?: Partial<SearchFilters>
): Promise<ApiResponse<TVSeries>> => {
  try {
    const params: any = { 
      query, 
      page,
      include_adult: false 
    };

    if (filters?.year) {
      params['first_air_date_year'] = filters.year;
    }
    if (filters?.rating) {
      params['vote_average.gte'] = filters.rating;
    }

    const response = await tmdbApi.get('/search/tv', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching TV series:', error);
    throw error;
  }
};

/**
 * Get movie details
 */
export const getMovieDetails = async (movieId: number): Promise<DetailedMovie> => {
  try {
    const response = await tmdbApi.get(`/movie/${movieId}`, {
      params: {
        append_to_response: 'credits,videos,images'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching movie details:', error);
    throw error;
  }
};

/**
 * Get TV series details
 */
export const getTVSeriesDetails = async (tvId: number): Promise<DetailedTVSeries> => {
  try {
    const response = await tmdbApi.get(`/tv/${tvId}`, {
      params: {
        append_to_response: 'credits,videos,images'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching TV series details:', error);
    throw error;
  }
};

/**
 * Get movie credits (cast and crew)
 */
export const getMovieCredits = async (movieId: number): Promise<Credits> => {
  try {
    const response = await tmdbApi.get(`/movie/${movieId}/credits`);
    return response.data;
  } catch (error) {
    console.error('Error fetching movie credits:', error);
    throw error;
  }
};

/**
 * Get TV series credits (cast and crew)
 */
export const getTVSeriesCredits = async (tvId: number): Promise<Credits> => {
  try {
    const response = await tmdbApi.get(`/tv/${tvId}/credits`);
    return response.data;
  } catch (error) {
    console.error('Error fetching TV series credits:', error);
    throw error;
  }
};

/**
 * Get movie genres
 */
export const getMovieGenres = async (): Promise<{ genres: Genre[] }> => {
  try {
    const response = await tmdbApi.get('/genre/movie/list');
    return response.data;
  } catch (error) {
    console.error('Error fetching movie genres:', error);
    throw error;
  }
};

/**
 * Get TV genres
 */
export const getTVGenres = async (): Promise<{ genres: Genre[] }> => {
  try {
    const response = await tmdbApi.get('/genre/tv/list');
    return response.data;
  } catch (error) {
    console.error('Error fetching TV genres:', error);
    throw error;
  }
};

/**
 * Get movies by genre
 */
export const getMoviesByGenre = async (genreId: number, page: number = 1): Promise<ApiResponse<Movie>> => {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: {
        with_genres: genreId,
        page,
        sort_by: 'popularity.desc'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching movies by genre:', error);
    throw error;
  }
};

/**
 * Get TV series by genre
 */
export const getTVSeriesByGenre = async (genreId: number, page: number = 1): Promise<ApiResponse<TVSeries>> => {
  try {
    const response = await tmdbApi.get('/discover/tv', {
      params: {
        with_genres: genreId,
        page,
        sort_by: 'popularity.desc'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching TV series by genre:', error);
    throw error;
  }
}; 